import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Star, Eye, EyeOff, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  occasion: '',
  rating: 5,
  text: '',
  is_featured: true,
  is_active: true,
  display_order: 0,
};

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // testimonial object or {} for new
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
  });

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/testimonials`, { headers: authHeaders() });
      setItems(res.data.testimonials || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expired');
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load testimonials');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm, display_order: (items[items.length - 1]?.display_order || 0) + 1 });
    setEditing({});
  };

  const openEdit = (t) => {
    setForm({
      name: t.name || '',
      occasion: t.occasion || '',
      rating: t.rating || 5,
      text: t.text || '',
      is_featured: !!t.is_featured,
      is_active: !!t.is_active,
      display_order: t.display_order || 0,
    });
    setEditing(t);
  };

  const closeModal = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.occasion || !form.text) {
      toast.error('Name, occasion and quote are required');
      return;
    }
    setSaving(true);
    try {
      if (editing && editing.id) {
        const res = await axios.patch(`${API}/api/admin/testimonials/${editing.id}`, form, { headers: authHeaders() });
        setItems(items.map((i) => (i.id === editing.id ? res.data.testimonial : i)));
        toast.success('Testimonial updated');
      } else {
        const res = await axios.post(`${API}/api/admin/testimonials`, form, { headers: authHeaders() });
        setItems([...items, res.data.testimonial]);
        toast.success('Testimonial added');
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      const res = await axios.patch(
        `${API}/api/admin/testimonials/${t.id}`,
        { is_active: !t.is_active },
        { headers: authHeaders() }
      );
      setItems(items.map((i) => (i.id === t.id ? res.data.testimonial : i)));
      toast.success(t.is_active ? 'Hidden from website' : 'Now visible on website');
    } catch (err) {
      toast.error('Failed to update visibility');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete testimonial from ${t.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/testimonials/${t.id}`, { headers: authHeaders() });
      setItems(items.filter((i) => i.id !== t.id));
      toast.success('Testimonial deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Testimonials</h1>
          <p className="text-gray-400">Add, edit and feature customer reviews shown on the homepage hero</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="testimonial-add-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/50"
        >
          <Plus size={18} />
          Add Testimonial
        </button>
      </div>

      {/* List */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            No testimonials yet. Click "Add Testimonial" to add the first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Order</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Quote</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Visible</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {items.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-800/30 transition-colors">
                    <td className="px-6 py-4 text-gray-300">{t.display_order}</td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{t.name}</div>
                      <div className="text-sm text-gray-400">{t.occasion}</div>
                      {t.source === 'customer-submission' && !t.is_active && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded text-[10px] uppercase tracking-wider font-semibold">
                          Pending Approval
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <p className="text-sm text-gray-300 line-clamp-2">"{t.text}"</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <Star key={i} size={14} className="text-amber-400" fill="currentColor" />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(t)}
                        data-testid={`testimonial-toggle-${t.id}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          t.is_active
                            ? 'bg-green-500/20 text-green-400 border-green-500/50 hover:bg-green-500/30'
                            : 'bg-gray-700/50 text-gray-400 border-gray-600 hover:bg-gray-700'
                        }`}
                      >
                        {t.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                        {t.is_active ? 'Visible' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(t)}
                          data-testid={`testimonial-edit-${t.id}`}
                          className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          data-testid={`testimonial-delete-${t.id}`}
                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {editing.id ? 'Edit Testimonial' : 'Add Testimonial'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                <X size={24} className="text-gray-400" />
              </button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="testimonial-name-input"
                    className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none"
                    placeholder="Sarah Thompson"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Occasion</label>
                  <input
                    type="text"
                    value={form.occasion}
                    onChange={(e) => setForm({ ...form, occasion: e.target.value })}
                    data-testid="testimonial-occasion-input"
                    className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none"
                    placeholder="Mother's Memorial"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Quote</label>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  rows={4}
                  data-testid="testimonial-text-input"
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30 outline-none resize-none"
                  placeholder="What the customer said about your work..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Rating</label>
                  <select
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value, 10) })}
                    className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 outline-none"
                  >
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n}>
                        {'★'.repeat(n)} {n} star{n > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Display Order</label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-amber-500 outline-none"
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <label className="flex items-center gap-2 px-4 py-3 bg-gray-800 rounded-lg border border-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 accent-amber-500"
                    />
                    <span className="text-white">Visible on website</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2.5 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="testimonial-save-btn"
                  className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editing.id ? 'Save Changes' : 'Add Testimonial'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
